import React from 'react';
import { Autocomplete, Checkbox, FormControlLabel, TextField } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MainButton from '../../../../common/buttons/MainButton';
import colors from '../../../../../scss/colors';
import { responsiveSizeTimesheet } from '../../../../../utils/constants';
import CustomDayPicker from '../../../../common/CustomDayPicker/CustomDayPicker';
import { generalEnums } from '../../../../../utils/enums';
import CommonFileUploader from '../../../../fileupload/CommonFileUploader/CommonFileUploader';
import { AttachmentItem } from '../../../timesheet/Attachments/Attachments';
import { buttonVariants } from '../../../../common/buttons/types';

const textFieldStyles = (width, radius) => ({
  borderRadius: radius,
  width,
  background: colors.WHITE,
  color: colors.BLACK,
  fieldset: {
    border: `1px solid ${colors.LIGHT_GREY}`,
    borderRadius: radius,
    legend: {
      width: 'unset'
    }
  }
});

const textAreaStyles = () => ({
  ...textFieldStyles(),
  width: '100%',
  '& .MuiOutlinedInput-input.MuiInputBase-input': {
    height: '50px'
  }
});

const Categories = React.memo(({ t, classes, category, expense, action = () => {}, expensestypes }) => {
  return (
    <div className={classes.mobileItemWrapperSection}>
      <h5 className={classes.title}>{t('dashboard.expenses.table.category')}*</h5>
      <div className={classes.content}>
        {expensestypes?.map((types) => {
          return (
            <MainButton
              key={types.key}
              size="small"
              variant={types.label === expense?.Category?.Name ? buttonVariants.SELECTED : buttonVariants.TERNARY}
              outlined={types.label !== expense?.Category?.Name}
              rounded
              action={() => action(types, expense?.Id)}
            >
              {types.label}
            </MainButton>
          );
        })}
      </div>
    </div>
  );
});

const DayPicker = React.memo(({ t, classes, value, action }) => {
  return (
    <div className={classes.mobileItemWrapperSection}>
      <h5 className={classes.title}>{t('dashboard.expenses.table.date')}</h5>
      <div className={classes.content}>
        <CustomDayPicker
          date={value}
          dayFormat={generalEnums.DATEANDMONTH}
          changeHandler={(newValue) => action(newValue)}
          sxAdornment={{
            position: 'absolute',
            right: '35px'
          }}
          sx={{
            width: '100%',
            background: colors.NAVY_BLUE,
            borderRadius: '100px',
            height: '35px',
            '& .MuiOutlinedInput-input.MuiInputBase-input': {
              color: colors.WHITE,
              textAlign: 'center'
            },
            '& .MuiSvgIcon-root': {
              color: colors.WHITE
            },
            '& .MuiOutlinedInput-root.MuiInputBase-root': {
              borderRadius: '100px',
              height: '100%'
            }
          }}
        />
      </div>
    </div>
  );
});

const CostCode = React.memo(({ t, classes, value, action }) => {
  return (
    <div className="pb-6 border-b border-[#D1D3D4] mt-5 flex flex-col">
      <h5 className={classes.title}>{t('dashboard.expenses.table.code')}</h5>
      <div className={classes.content}>
        <TextField
          placeholder={t('dashboard.expenses.table.code')}
          value={value}
          onChange={action}
          sx={textFieldStyles('100%')}
        />
      </div>
    </div>
  );
});

const Comment = React.memo(({ t, classes, value, action }) => {
  return (
    <div className={classes.mobileItemWrapperSection}>
      <h5 className={classes.title}>{t('dashboard.expenses.table.comment')}</h5>
      <div className={classes.content}>
        <TextField
          placeholder={t('dashboard.expenses.table.comment')}
          value={value}
          onChange={action}
          sx={textAreaStyles()}
        />
      </div>
    </div>
  );
});

const ReceiptUploader = React.memo(({ t, classes, qty, action }) => {
  return (
    <div className={`${classes.mobileItemWrapperSection} ${classes.lastChild}`}>
      <h5 className={classes.title}>{t('dashboard.expenses.table.receipt')}</h5>
      <div className={classes.content}>
        <CommonFileUploader
          uploadHandler={(file) => action(file)}
          isMultiple={false}
          acceptedFormats={['.jpeg', '.jpg', '.png', '.PNG', '.pdf', '.doc', '.docx', '.csv', '.xls', 'xlsx']}
          maxFileSizeMB={5}
          allowedQtyOfAttachments={10}
          existingAttachmentsQty={qty}
        />
      </div>
    </div>
  );
});

const ExpenseAmount = React.memo(({ t, expense, category, value, action }) => {
  return (
    <TextField
      type="number"
      placeholder={
        expense?.CategoryId === 5 || category.value === 5
          ? t('common.general.miles')
          : t('dashboard.expenses.table.expenseamount')
      }
      value={value}
      onChange={action}
      sx={textFieldStyles('80px', '10px 0px 0px 10px')}
    />
  );
});

const AttachmentItems = React.memo(({ id, label, action }) => {
  return <AttachmentItem key={id} label={label} deleteHandler={action} />;
});

const NoReceiptCheckbox = React.memo(({ checked, label, action }) => {
  return (
    <FormControlLabel
      control={<Checkbox checked={checked} onChange={action} />}
      label={label}
      sx={{ margin: 0, 'MuiFormControlLabel-label': { fontSize: 9 } }}
    />
  );
});

const SaveandUpdateButton = React.memo(({ t, size, action, fxRate }) => {
  return (
    <MainButton
      rounded
      size="xsmall"
      forceWidth={size.width > responsiveSizeTimesheet ? '204px' : '100%'}
      action={action}
    >
      {fxRate > 0 ? t('common.general.update') : t('common.general.save')}
    </MainButton>
  );
});

const Rate = React.memo(({ t, expense, action, value, disability = false }) => {
  return (
    <TextField
      type="number"
      disabled={
        disability &&
        expense?.CategoryId !== 5 &&
        (expense?.FxCurrency?.CurrencyName
          ? expense?.ExpenseCurrency?.CurrencyName === expense?.FxCurrency?.CurrencyName
          : true)
      }
      placeholder={t('dashboard.expenses.table.rate')}
      value={value}
      onChange={action}
      sx={textFieldStyles('80px', '10px 0px 0px 10px')}
    />
  );
});

const Currencies = React.memo(({ value, currencies = [], action }) => {
  return (
    <Autocomplete
      disableClearable
      value={value || null}
      options={currencies}
      sx={textFieldStyles('100px', '0px 10px 10px 0px')}
      popupIcon={<ExpandMoreIcon />}
      forcePopupIcon
      renderOption={(props, option) => (
        <li {...props} key={option.key}>
          {option.label}
        </li>
      )}
      onChange={action}
      renderInput={(params) => <TextField {...params} sx={textFieldStyles('100%')} />}
    />
  );
});

export {
  Categories,
  ExpenseAmount,
  Rate,
  Currencies,
  DayPicker,
  Comment,
  CostCode,
  ReceiptUploader,
  AttachmentItems,
  NoReceiptCheckbox,
  SaveandUpdateButton
};
